import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import { SmsForm } from '../Form';
import containerQueryProps from '../../props/containerQueryProps';
import {
  getPhoneValidationError,
} from '../../utils/phoneValidation';

import './Auth.scss';
import { AuthAnalyticsEvents, AuthFlowType, SignInMethods } from '../../containers/AuthContainer/authSteps';
import TargetedDelivery from '../Experiments/TargetedDelivery';
import useExperiment from '../Experiments/useExperiment';
import { RESY_WEB_DEPRECATE_EMAIL_PASSWORD_LOGIN } from '../../constants/optimizelyExperiments';
import OPENAI_WIDGET from '../../../../widget/app/modules/constants/srcTypes';
import {
  AUTH_ACTIONS,
  AUTH_FLOWS,
  AUTH_METHODS,
  AUTH_STEPS,
  buildAuthDdActionName,
  trackAuthDdAction,
} from '../../utils/authDatadog';

const SmsView = ({
  onSubmit,
  onUseEmail,
  onEmailCode,
  countryCodes,
  containerQuery,
  venue,
  defaultCountryCode,
  event,
  phone,
  authFlowType,
  sourceId,
}) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [phoneValidationError, setPhoneValidationError] = useState(null);
  const [disableContinue, setDisableContinue] = useState(false);
  const [hasEditedPhoneSinceSubmit, setHasEditedPhoneSinceSubmit] = useState(false);
  const { sm } = containerQuery;
  const footNoteRef = useRef(null);
  const authFootNoteRef = useRef(null);

  const requiresCaptcha = (code) => {
    const country = countryCodes?.find(
      // eslint-disable-next-line camelcase
      (val) => val.code === code
    );
    return !!(country && country.captcha);
  };

  const getGrecaptcha = () => {
    if (window.grecaptcha && window.grecaptcha.render) {
      return window.grecaptcha;
    }

    return undefined;
  };

  const renderCaptcha = () => {
    const grecaptcha = getGrecaptcha();

    if (!grecaptcha) {
      setTimeout(renderCaptcha, 500);
      return;
    }
    grecaptcha.render('captcha__container', {
      sitekey: '6Lfw-dIZAAAAAESRBH4JwdgfTXj5LlS1ewlvvCYe',
    });
  };

  const checkShowCaptcha = () => {
    const isCaptcha = requiresCaptcha(selectedCountryCode);

    if (showCaptcha !== isCaptcha) {
      setShowCaptcha(isCaptcha);
    }
  };

  useEffect(() => {
    event(AuthAnalyticsEvents.START_SIGN_IN, {
      method: SignInMethods.PHONE,
    });
  }, []);

  useEffect(() => {
    if (showCaptcha) {
      renderCaptcha();
    }
  }, [showCaptcha]);

  useEffect(() => {
    checkShowCaptcha();
  }, [countryCodes]);

  const openLegalLink = (link) => {
    const url = link?.getAttribute('href');
    if (!url) {
      return;
    }

    if (sourceId === OPENAI_WIDGET) {
      window?.parent?.postMessage({ name: 'resy-external', link: url }, '*');
    }

    window?.open(url);
  };

  useEffect(() => {
    const footNoteElement = footNoteRef?.current;
    const authFootNoteElement = authFootNoteRef?.current;
    const footNoteLinks = footNoteElement?.querySelectorAll('a[href]') || [];
    const authFootNoteLinks = authFootNoteElement?.querySelectorAll('a[href]') || [];
    const links = [...footNoteLinks, ...authFootNoteLinks];

    const setUpLinks = Array.from(links).map((link) => {
      const handleLinkClick = (e) => {
        e.preventDefault();
        openLegalLink(link);
      };

      link.onclick = handleLinkClick;

      return () => {
        link.onclick = null;
      };
    });

    return () => {
      setUpLinks.forEach((cleanup) => cleanup());
    };
  }, [venue]);

  const checkCaptchaResponse = () => {
    const grecaptcha = getGrecaptcha();
    const captchaResponse = grecaptcha?.getResponse();

    return captchaResponse;
  };

  const onChange = (values) => {
    // eslint-disable-next-line camelcase
    const { country_code } = values;
    /** use the country to determine if captcha is relevant
     * this is result of the great texting scam of 2021
     * in some countries, scammers were using resy to send texts
     * whereby they would make money because they owned the lines and
     * collected fees on each text, causing Resy to rack up > $200,000
     * in fees over a few days(!)
     */
    setShowCaptcha(requiresCaptcha(country_code));
    setSelectedCountryCode(country_code);
    const nextPhoneValidationError = getPhoneValidationError(
      values,
      { fieldName: 'phone' }
    );
    setPhoneValidationError(nextPhoneValidationError);
    setDisableContinue(Boolean(nextPhoneValidationError));
    setHasEditedPhoneSinceSubmit(true);
  };

  const onSmsSubmit = async (data) => {
    setHasEditedPhoneSinceSubmit(false);
    const phoneError = getPhoneValidationError(
      data,
      { validateIncomplete: true, fieldName: 'phone' }
    );
    if (phoneError) {
      setPhoneValidationError(phoneError);
      setDisableContinue(true);
      throw { data: { mobile_number: phoneError } }; // eslint-disable-line no-throw-literal
    }

    setPhoneValidationError(null);
    setDisableContinue(false);
    const args = [data];
    if (requiresCaptcha(data?.country_code)) {
      args.push(checkCaptchaResponse());
    }
    try {
      await onSubmit(...args);
    } catch (err) {
      // Mostly this error gets rethrown, but first we check the error
      // to set the disableContinue state before rethrowing.
      const serverPhoneError = err?.data?.data?.mobile_number || err?.data?.mobile_number;
      if (serverPhoneError) {
        setDisableContinue(true);
      }
      throw err;
    }
  };

  const headlineCopy = () => {
    if (authFlowType === AuthFlowType.VERIFY_PHONE_TO_SIGN_UP) {
      return getTranslation({ translationKey: 'onboarding.enter_number_for_sign_up', fallback: 'To create an account, please enter your mobile number:' });
    }
    return getTranslation({ translationKey: 'onboarding.enter_number', fallback: 'Please enter your mobile phone number to verify or create an account:' });
  };

  const footerContent = () => {
    if (authFlowType === AuthFlowType.VERIFY_PHONE_TO_SIGN_UP) {
      return null;
    }
    const [isFFEnabled] = useExperiment(RESY_WEB_DEPRECATE_EMAIL_PASSWORD_LOGIN);

    return (
      <>
        <div className="AuthView__Divider" role="separator" />
        <div
          className={cx('AuthView__Footer SmsViewFooter', {
            'AuthView__Footer--sm': sm,
          })}
        >
          {!isFFEnabled && (
            <TranslationProvider
              translationKey="onboarding.sign_in"
            >
              <button
                onClick={() => {
                  const actionName = buildAuthDdActionName({
                    flow: AUTH_FLOWS.SIGN_IN,
                    method: AUTH_METHODS.EMAIL_PASS,
                    step: AUTH_STEPS.CAPTURE_PHONE,
                    action: AUTH_ACTIONS.SWITCH_METHOD,
                  });
                  trackAuthDdAction(actionName, { source: 'auth' });
                  onUseEmail();
                }}
                type="button"
                className="SmsViewSignInButton"
                data-dd-action-name={buildAuthDdActionName({
                  flow: AUTH_FLOWS.SIGN_IN,
                  method: AUTH_METHODS.EMAIL_PASS,
                  step: AUTH_STEPS.CAPTURE_PHONE,
                  action: AUTH_ACTIONS.SWITCH_METHOD,
                })}
              >
                Log in with email & password
              </button>
            </TranslationProvider>
          )}
          <TargetedDelivery flagKey="login_optimization">
            <TranslationProvider translationKey="onboarding.sign_in_email_code">
              <button
                onClick={() => {
                  const actionName = buildAuthDdActionName({
                    flow: AUTH_FLOWS.SIGN_IN,
                    method: AUTH_METHODS.EMAIL_CODE,
                    step: AUTH_STEPS.CAPTURE_PHONE,
                    action: AUTH_ACTIONS.REQUEST_EMAIL_CODE,
                  });
                  trackAuthDdAction(actionName, { source: 'auth' });
                  onEmailCode();
                }}
                type="button"
                className="SmsViewSignInButtonCode"
                data-testid="sign-in-email-code"
                data-dd-action-name={buildAuthDdActionName({
                  flow: AUTH_FLOWS.SIGN_IN,
                  method: AUTH_METHODS.EMAIL_CODE,
                  step: AUTH_STEPS.CAPTURE_PHONE,
                  action: AUTH_ACTIONS.REQUEST_EMAIL_CODE,
                })}
              >
                Log in with email & one-time code
              </button>
            </TranslationProvider>
          </TargetedDelivery>
        </div>
      </>
    );
  };

  return (
    <div className={cx('AuthView', {
      'AuthView--sm': sm,
    })}
    >
      <TranslationProvider
        translationKey={venue ? 'onboarding.title' : 'onboarding.title_secondary'}
        translationValues={{
          venueName: venue,
        }}
      >
        <div className="AuthView__Text--light SmsViewTextDesc">
          Resy’s hospitality platform securely manages your account information and reservations.
        </div>
      </TranslationProvider>
      <h2 className="AuthView__Headline SmsViewHeadline">
        {headlineCopy()}
      </h2>
      <SmsForm
        onFormValueChange={onChange}
        onSubmit={onSmsSubmit}
        countryCodes={countryCodes}
        defaultCountryCode={defaultCountryCode}
        phone={phone}
        showCaptcha={showCaptcha}
        clientPhoneError={phoneValidationError}
        disableSubmit={disableContinue}
        suppressServerPhoneError={hasEditedPhoneSinceSubmit}
        datadogActionName={buildAuthDdActionName({
          flow: authFlowType === AuthFlowType.VERIFY_PHONE_TO_SIGN_UP ? AUTH_FLOWS.SIGN_UP : AUTH_FLOWS.SIGN_IN,
          method: AUTH_METHODS.PHONE_CODE,
          step: AUTH_STEPS.CAPTURE_PHONE,
          action: AUTH_ACTIONS.CONTINUE,
        })}
      />

      <div
        ref={authFootNoteRef}
      >
        <TranslationProvider
          translationKey={venue ? 'onboarding.footnote' : 'onboarding.footnote_secondary'}
          translationValues={{
            venueName: venue,
          }}
        >
          <div className={cx('AuthView__Footnote', {
            'AuthView__Footnote--sm': sm,
          })}
          >
            Resy will only use this number to confirm and manage your reservation.
          </div>
        </TranslationProvider>
      </div>

      {venue && (
        <div
          ref={footNoteRef}
        >
          <TranslationProvider
            translationKey="onboarding.footnote_secondary"
          >
            <div
              className={cx('AuthView__Footnote', {
                'AuthView__Footnote--sm': sm,
              })}
            >
              By providing your mobile number, you agree to the Resy
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              and
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              , and your mobile number may be used to send you text messages and call you, through the use of an auto dialer, artificial,
              and/or prerecorded call or text, to service your Resy account including to confirm and manage reservations. Message and data
              rates may apply. Message frequency varies. You may opt out of receiving text messages at any time by replying STOP. Text HELP for help

            </div>
          </TranslationProvider>
        </div>
      )}
      {footerContent()}
    </div>
  );
};

SmsView.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onUseEmail: PropTypes.func.isRequired,
  onEmailCode: PropTypes.func.isRequired,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({
    iso3166: PropTypes.string,
    captcha: PropTypes.bool,
    code: PropTypes.string,
    country_name: PropTypes.string,
  })),
  containerQuery: containerQueryProps,
  venue: PropTypes.string,
  defaultCountryCode: PropTypes.string,
  event: PropTypes.func,
  phone: PropTypes.string,
  authFlowType: PropTypes.string,
  sourceId: PropTypes.string,
};

SmsView.defaultProps = {
  countryCodes: undefined,
  containerQuery: {},
  venue: null,
  defaultCountryCode: '1',
  event: () => { },
  phone: '',
  authFlowType: AuthFlowType.DEFAULT,
  sourceId: '',
};

SmsView.displayName = 'SmsView';
export default SmsView;
